import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  fetchAdminOrder,
  fetchAdminOrders,
  updateOrderStatus,
} from '../lib/adminApi';
import { formatPrice } from '../../utils/formatPrice';

const FULFILMENT_STATUSES = ['unfulfilled', 'processing', 'shipped', 'fulfilled', 'cancelled'];
const PAYMENT_STATUSES = ['pending', 'paid', 'refunded', 'failed'];
const ORDER_STATUSES = ['pending', 'completed', 'cancelled'];

function OrdersList() {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [query, setQuery] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await fetchAdminOrders();
        if (!cancelled) setOrders(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Could not load orders.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return orders;
    return orders.filter(order =>
      (order.order_number || '').toLowerCase().includes(q) ||
      (order.shipping_name || '').toLowerCase().includes(q) ||
      (order.shipping_email || '').toLowerCase().includes(q)
    );
  }, [orders, query]);

  return (
    <div>
      <div style={{ marginBottom: 24 }}>
        <h1 className="admin-page-title">Orders</h1>
        <p className="admin-page-subtitle">Track payments and fulfilment</p>
      </div>

      <div className="admin-card">
        <div className="admin-toolbar">
          <input
            className="admin-search"
            placeholder="Search orders..."
            value={query}
            onChange={e => setQuery(e.target.value)}
          />
        </div>
        {error && <p className="admin-error">{error}</p>}
        {loading && <p className="admin-empty">Loading orders...</p>}
        {!loading && (
          <div className="admin-table-wrap">
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Customer</th>
                  <th>Date</th>
                  <th>Products</th>
                  <th>Total</th>
                  <th>Payment</th>
                  <th>Fulfilment</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(order => (
                  <tr key={order.id}>
                    <td>
                      <Link to={`/admin/orders/${order.id}`}>
                        {order.order_number || order.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td>
                      <div>{order.shipping_name}</div>
                      <div style={{ color: '#777', fontSize: 12 }}>{order.shipping_email}</div>
                    </td>
                    <td>{new Date(order.created_at).toLocaleDateString()}</td>
                    <td>{(order.order_items || []).reduce((s, i) => s + i.quantity, 0)} items</td>
                    <td>{formatPrice(order.total)}</td>
                    <td>
                      <span className={`admin-badge ${order.payment_status || 'paid'}`}>
                        {order.payment_status || 'paid'}
                      </span>
                    </td>
                    <td>
                      <span className={`admin-badge ${order.fulfilment_status || 'unfulfilled'}`}>
                        {order.fulfilment_status || 'unfulfilled'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {!filtered.length && <p className="admin-empty">No orders found.</p>}
          </div>
        )}
      </div>
    </div>
  );
}

function OrderDetail() {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      try {
        const data = await fetchAdminOrder(orderId);
        if (!cancelled) setOrder(data);
      } catch (err) {
        if (!cancelled) setError(err.message || 'Order not found.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [orderId]);

  const save = async () => {
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const updated = await updateOrderStatus(orderId, {
        status: order.status,
        payment_status: order.payment_status,
        fulfilment_status: order.fulfilment_status,
        notes: order.notes || null,
      });
      setOrder(current => ({ ...current, ...updated }));
      setSuccess('Order updated.');
    } catch (err) {
      setError(err.message || 'Could not update order.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="admin-empty">Loading order...</p>;
  if (!order) return <p className="admin-error">{error || 'Order not found.'}</p>;

  return (
    <div>
      <div className="admin-topbar" style={{ marginBottom: 24 }}>
        <div>
          <h1 className="admin-page-title">{order.order_number || 'Order detail'}</h1>
          <p className="admin-page-subtitle">
            Placed {new Date(order.created_at).toLocaleString()}
          </p>
        </div>
        <button type="button" className="admin-btn admin-btn-secondary" onClick={() => navigate('/admin/orders')}>
          Back
        </button>
      </div>

      {error && <p className="admin-error">{error}</p>}
      {success && <p className="admin-success-msg">{success}</p>}

      <div className="admin-grid-2">
        <div className="admin-card">
          <h2 className="admin-card-title">Customer</h2>
          <p><strong>{order.shipping_name}</strong></p>
          <p style={{ color: '#777' }}>{order.shipping_email}</p>
          <p style={{ marginTop: 12 }}>
            {order.shipping_address}<br />
            {order.shipping_city}, {order.shipping_zip}
          </p>
        </div>

        <div className="admin-card">
          <h2 className="admin-card-title">Status</h2>
          <div className="admin-field">
            <label className="admin-label">Order status</label>
            <select
              className="admin-input"
              value={order.status || 'pending'}
              onChange={e => setOrder(o => ({ ...o, status: e.target.value }))}
            >
              {ORDER_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="admin-field">
            <label className="admin-label">Payment status</label>
            <select
              className="admin-input"
              value={order.payment_status || 'paid'}
              onChange={e => setOrder(o => ({ ...o, payment_status: e.target.value }))}
            >
              {PAYMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="admin-field">
            <label className="admin-label">Fulfilment status</label>
            <select
              className="admin-input"
              value={order.fulfilment_status || 'unfulfilled'}
              onChange={e => setOrder(o => ({ ...o, fulfilment_status: e.target.value }))}
            >
              {FULFILMENT_STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>
          <div className="admin-field">
            <label className="admin-label">Notes</label>
            <textarea
              className="admin-textarea"
              value={order.notes || ''}
              onChange={e => setOrder(o => ({ ...o, notes: e.target.value }))}
            />
          </div>
          <button type="button" className="admin-btn admin-btn-primary" onClick={save} disabled={saving}>
            {saving ? 'Saving...' : 'Save changes'}
          </button>
        </div>
      </div>

      <div className="admin-card">
        <h2 className="admin-card-title">Products</h2>
        <div className="admin-table-wrap">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Item</th>
                <th>Qty</th>
                <th>Price</th>
                <th>Line total</th>
              </tr>
            </thead>
            <tbody>
              {(order.order_items || []).map(item => (
                <tr key={item.id}>
                  <td>
                    <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                      {item.image_url && <img src={item.image_url} alt="" className="admin-thumb" />}
                      <span>
                        {item.product_name}
                        {item.size_label ? ` · ${item.size_label}` : ''}
                      </span>
                    </div>
                  </td>
                  <td>{item.quantity}</td>
                  <td>{formatPrice(item.price)}</td>
                  <td>{formatPrice(item.price * item.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p style={{ textAlign: 'right', fontWeight: 600, fontSize: 18, marginTop: 16 }}>
          Total: {formatPrice(order.total)}
        </p>
      </div>
    </div>
  );
}

function Orders() {
  const { orderId } = useParams();
  if (orderId) return <OrderDetail />;
  return <OrdersList />;
}

export default Orders;
